import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

/**
 * Global gate that checks if the current GUEST user is blocked.
 * Mirrors BlockedUserGate but for guest sessions.
 *
 * Detection mechanisms (same pattern as BlockedUserGate):
 * 1. Initial check when guest session starts (via Edge Function)
 * 2. Realtime subscription on user_blocklist table changes
 * 3. Broadcast channel listener for server-side block notifications
 * 4. Debounced focus-check when app has been hidden >60s
 *
 * When blocked: redirects to /blocked with identifierHash + organizationId in state.
 */

const MIN_HIDDEN_DURATION = 60_000;

export function BlockedGuestGate({ children }: { children: React.ReactNode }) {
  const { isGuest, guestData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hiddenSinceRef = useRef<number | null>(null);
  const isCheckingRef = useRef(false);

  const exemptPaths = ['/blocked', '/auth', '/bankid-callback', '/onboarding'];

  const identifierHash = guestData?.identifierHash;
  const organizationId = guestData?.organizationId;
  const sessionId = guestData?.sessionId;

  const handleGuestBlocked = useCallback((message?: string) => {
    console.log('[BlockedGuestGate] Guest is blocked, redirecting to /blocked');
    navigate('/blocked', {
      replace: true,
      state: {
        identifierHash,
        organizationId,
        message: message || 'Du har inte behörighet att handla hos denna butik.',
      },
    });
  }, [navigate, identifierHash, organizationId]);

  // Check block status via Edge Function
  const checkGuestBlocked = useCallback(async () => {
    if (!identifierHash || !organizationId) return;
    if (exemptPaths.some(p => location.pathname.startsWith(p))) return;
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    try {
      const { data, error } = await supabase.functions.invoke('api-sessions/guest/block-check', {
        body: {
          identifier_hash: identifierHash,
          organization_id: organizationId,
          session_id: sessionId,
        },
      });

      if (error) {
        // Supabase JS sets data=null on non-2xx responses. Try to parse blocked
        // status from the error body as a fallback (defense-in-depth).
        try {
          const errorBody = JSON.parse(error.message);
          if (errorBody?.blocked === true) {
            handleGuestBlocked(errorBody.message);
            return;
          }
        } catch {
          // Not JSON — genuine network/server error
        }
        console.warn('[BlockedGuestGate] Error checking block status:', error);
        return;
      }

      if (data?.blocked === true) {
        handleGuestBlocked(data.message);
      }
    } catch (err) {
      console.warn('[BlockedGuestGate] Exception checking block status:', err);
    } finally {
      isCheckingRef.current = false;
    }
  }, [identifierHash, organizationId, sessionId, location.pathname, handleGuestBlocked]);

  // Initial check when guest data becomes available
  useEffect(() => {
    if (!isGuest || !identifierHash || !organizationId) return;
    checkGuestBlocked();
  }, [isGuest, identifierHash, organizationId, checkGuestBlocked]);

  // Focus-check — only after app hidden for >60s
  useEffect(() => {
    if (!isGuest || !identifierHash) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const hiddenDuration = hiddenSinceRef.current
          ? Date.now() - hiddenSinceRef.current
          : 0;
        hiddenSinceRef.current = null;

        if (hiddenDuration >= MIN_HIDDEN_DURATION) {
          console.log(`[BlockedGuestGate] App visible after ${Math.round(hiddenDuration / 1000)}s — checking`);
          checkGuestBlocked();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isGuest, identifierHash, checkGuestBlocked]);

  // Broadcast channel — server sends this when blocking a guest
  useEffect(() => {
    if (!isGuest || !identifierHash || !organizationId) return;

    // Channel keyed by hash prefix + org to avoid collisions
    const channelName = `guest-blocked-${identifierHash.substring(0, 16)}-${organizationId.substring(0, 8)}`;
    console.log('[BlockedGuestGate] Setting up broadcast listener:', channelName);

    const channel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'guest_blocked' }, (payload) => {
        console.log('[BlockedGuestGate] Received blocked broadcast:', payload);
        handleGuestBlocked(payload.payload?.message);
      })
      .subscribe();

    return () => {
      console.log('[BlockedGuestGate] Cleaning up broadcast listener');
      supabase.removeChannel(channel);
    };
  }, [isGuest, identifierHash, organizationId, handleGuestBlocked]);

  // Realtime subscription on user_blocklist (if table is in Realtime publication)
  useEffect(() => {
    if (!isGuest || !identifierHash || !organizationId) return;

    console.log('[BlockedGuestGate] Setting up realtime subscription for user_blocklist');

    const channel = supabase
      .channel(`guest-blocklist-${identifierHash.substring(0, 16)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_blocklist',
          filter: `identifier_hash=eq.${identifierHash}`,
        },
        (payload) => {
          console.log('[BlockedGuestGate] Realtime blocklist change:', payload);
          const row = payload.new as any;
          if (row?.is_active === true && row?.organization_id === organizationId) {
            handleGuestBlocked();
          } else if (row?.is_active === false && location.pathname === '/blocked') {
            // Guest unblocked — redirect back to shop
            console.log('[BlockedGuestGate] Guest unblocked, redirecting to shop');
            const savedSlugPath = sessionStorage.getItem('last_slug_path') || '/shop';
            navigate(savedSlugPath.startsWith('/') ? savedSlugPath : '/' + savedSlugPath, { replace: true });
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[BlockedGuestGate] Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [isGuest, identifierHash, organizationId, handleGuestBlocked, location.pathname, navigate]);

  return <>{children}</>;
}
